import React, { useCallback, useEffect, useState } from "react";
import { Ban, Download, Pencil, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/constants";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Panel, Empty } from "@/pages/shared";

export default function AdminDeposits() {
  const { t } = useLang();
  const { user } = useAuth();
  const canEdit = ["admin", "superadmin"].includes(user?.role) && Number(user?.finance_class) >= 4;
  const [view, setView] = useState("month");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [data, setData] = useState(null);
  const [seq, setSeq] = useState(null);
  const [error, setError] = useState("");
  const [cancel, setCancel] = useState(null); // {id, number, reason}
  const [note, setNote] = useState(null); // {id, number, note}

  const load = useCallback(async () => {
    try {
      const q = view === "month" ? `month=${month}` : `year=${year}`;
      setData((await api.get(`/cash/deposits?${q}`)).data);
      setError("");
    } catch (e) { setData(null); setError(apiError(e.response?.data?.detail)); }
  }, [view, month, year]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/cash/deposit-settings").then(({ data: d }) => setSeq(d)).catch(() => {}); }, []);

  const saveSeq = async () => {
    try {
      const { data: d } = await api.put("/cash/deposit-settings", { next: Number(seq.next) });
      setSeq({ ...seq, ...d }); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doCancel = async () => {
    try {
      await api.post(`/cash/deposits/${cancel.id}/cancel`, { reason: cancel.reason });
      setCancel(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveNote = async () => {
    try {
      await api.put(`/cash/deposits/${note.id}`, { note: note.note });
      setNote(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const csv = () => {
    if (!data) return;
    const head = ["Nummer", "Datum", "Bedrag", "Gebracht door", "Bankrekening", "Herkomst", "Opmerking", "Geannuleerd"];
    const lines = data.rows.map((r) => [r.number, r.date, Number(r.amount).toFixed(2), r.by, r.bank_account,
      r.source === "closure" ? `dagafsluiting ${r.closure_date}` : "tussentijds", r.note || "",
      r.cancelled ? r.cancel_reason : ""]);
    const blob = new Blob([[head, ...lines].map((l) => l.join(";")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `afstortingen-${view === "month" ? month : year}.csv`;
    a.click();
  };

  return (
    <Panel
      title={t("admin_deposits")}
      testid="deposits-panel"
      action={
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <div className="flex rounded-full border border-border overflow-hidden">
            {[["month", t("hours_month")], ["year", t("season_year")]].map(([k, lbl]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`deposits-view-${k}`}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${view === k ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-muted"}`}>{lbl}</button>
            ))}
          </div>
          {view === "month"
            ? <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="deposits-month" />
            : <Input type="number" min="2000" max="2100" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" data-testid="deposits-year" />}
          <Button variant="outline" onClick={csv} data-testid="deposits-csv"><Download className="h-4 w-4 mr-1" />CSV</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="deposits-print"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
        </div>
      }
    >
      <div className="p-5 space-y-4">
        <p className="text-sm text-muted-foreground">{t("deposits_desc")}</p>
        {error && <p className="text-sm text-rose-600" data-testid="deposits-error">{error}</p>}

        {seq && (
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-muted/40 p-3 print:hidden" data-testid="deposits-seq">
            <div className="space-y-1">
              <Label className="text-xs">{t("deposits_next_number")}</Label>
              <Input type="number" min="1" value={seq.next} disabled={!canEdit} className="w-28" data-testid="deposits-next"
                onChange={(e) => setSeq({ ...seq, next: e.target.value })} />
            </div>
            <p className="text-sm font-semibold tabular-nums text-primary" data-testid="deposits-preview">
              {seq.year}-{String(Number(seq.next) || 1).padStart(3, "0")}
            </p>
            {canEdit && <Button variant="outline" size="sm" onClick={saveSeq} data-testid="deposits-seq-save">{t("save")}</Button>}
            <p className="text-[11px] text-muted-foreground flex-1 min-w-[14rem]">{t("deposits_seq_hint")}</p>
          </div>
        )}

        {data && (
          <p className="text-sm">
            {t("total")}: <span className="font-display font-bold tabular-nums text-primary" data-testid="deposits-total">{money(data.total)}</span>
            <span className="text-muted-foreground"> · {data.count} {t("admin_deposits").toLowerCase()}</span>
          </p>
        )}

        {!data || data.rows.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto">
            <Table data-testid="deposits-table">
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>{t("date")}</TableHead>
                  <TableHead className="text-right">{t("amount")}</TableHead>
                  <TableHead>{t("cash_deposit_by")}</TableHead>
                  <TableHead>{t("cash_deposit_account")}</TableHead>
                  <TableHead>{t("cash_deposit_origin")}</TableHead>
                  <TableHead>{t("note")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.id} data-testid={`deposit-row-${r.number}`} className={r.cancelled ? "opacity-60" : ""}>
                    <TableCell className="font-semibold tabular-nums text-primary">{r.number}</TableCell>
                    <TableCell className="whitespace-nowrap">{r.date}</TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${r.cancelled ? "line-through" : ""}`}>{money(r.amount)}</TableCell>
                    <TableCell>{r.by || "—"}</TableCell>
                    <TableCell className="text-xs tabular-nums">{r.bank_account || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.source === "closure" ? `${t("admin_cashclose")} ${r.closure_date}` : t("cash_deposit_interim")}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[14rem]">
                      {r.note || "—"}
                      {r.cancelled && <span className="block text-rose-600">{t("deposit_cancelled")}: {r.cancel_reason}</span>}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap print:hidden">
                      <Button variant="ghost" size="sm" data-testid={`deposit-print-${r.number}`}
                        onClick={() => window.open(`/print-afstorting?id=${r.id}`, "_blank")}><Printer className="h-4 w-4" /></Button>
                      {canEdit && !r.cancelled && (
                        <>
                          <Button variant="ghost" size="sm" data-testid={`deposit-note-${r.number}`}
                            onClick={() => setNote({ id: r.id, number: r.number, note: r.note || "" })}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" data-testid={`deposit-cancel-${r.number}`}
                            onClick={() => setCancel({ id: r.id, number: r.number, reason: "" })}><Ban className="h-4 w-4 text-rose-500" /></Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={!!cancel} onOpenChange={(o) => !o && setCancel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="deposit-cancel-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("deposit_cancel")} {cancel?.number}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t("deposit_cancel_hint")}</p>
            <Input required value={cancel?.reason || ""} placeholder={t("deposit_cancel_reason")} data-testid="deposit-cancel-reason"
              onChange={(e) => setCancel({ ...cancel, reason: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancel(null)}>{t("cancel")}</Button>
            <Button onClick={doCancel} disabled={!cancel?.reason?.trim()} data-testid="deposit-cancel-save">{t("deposit_cancel")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!note} onOpenChange={(o) => !o && setNote(null)}>
        <DialogContent className="sm:max-w-md" data-testid="deposit-note-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("note")} {note?.number}</DialogTitle></DialogHeader>
          <Input value={note?.note || ""} onChange={(e) => setNote({ ...note, note: e.target.value })} data-testid="deposit-note-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNote(null)}>{t("cancel")}</Button>
            <Button onClick={saveNote} data-testid="deposit-note-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
